import type { AxiosError } from 'axios'

interface ValidationError {
  field: string
  message: string
}

interface ApiErrorResponse {
  message?: string
  errors?: ValidationError[] | Record<string, string[]>
  error?: string
}

export const extractErrorMessage = (error: unknown): string => {
  if (!error) return 'Unknown error'

  if (typeof error === 'string') return error

  const axiosError = error as AxiosError<ApiErrorResponse>

  if (axiosError.response?.data) {
    const data = axiosError.response.data

    if (data.message) return data.message
    if (data.error) return data.error

    if (data.errors) {
      if (Array.isArray(data.errors)) {
        return data.errors.map((err) => err.message).join(', ')
      }
      const errMessages: string[] = []
      for (const field in data.errors) {
        errMessages.push(...data.errors[field])
      }
      return errMessages.join(', ')
    }
  }

  if ('message' in (error as Error)) {
    return (error as Error).message
  }

  return 'An unexpected error occurred'
}

export const handleApiError = (error: unknown): string => {
  console.error('API Error:', error)

  if (typeof error === 'string') {
    return error
  }

  const errorMessage = extractErrorMessage(error)
  return errorMessage || 'An unexpected error occurred'
}

export const logError = (message: string, error?: unknown): void => {
  console.error(message, error)
}

export const reportToMonitoring = (error: unknown): void => {
  console.error('[Error Monitoring]', error)
}

export const handleValidationErrors = (errors: Record<string, string[] | string | unknown>): Record<string, string> => {
  const normalizedErrors: Record<string, string> = {}

  for (const field in errors) {
    const value = errors[field]

    if (Array.isArray(value)) {
      normalizedErrors[field] = value[0]
    } else if (typeof value === 'string') {
      normalizedErrors[field] = value
    } else if (value && typeof value === 'object') {
      const nestedErrors = handleValidationErrors(value as Record<string, unknown>)
      for (const nestedField in nestedErrors) {
        normalizedErrors[nestedField] = nestedErrors[nestedField]
      }
    } else {
      normalizedErrors[field] = String(value || '')
    }
  }

  for (const field in normalizedErrors) {
    const message = normalizedErrors[field]
    if (field === 'email' || message.toLowerCase().includes('email')) {
      const emailExistsRegex = /user with email ['"]([^'"]+)['"] already exists/i
      const match = message.match(emailExistsRegex)

      if (match) {
        normalizedErrors.email = `User with email ${match[1]} already exists`
      }
    }
  }

  return normalizedErrors
}

export const ErrorHandler = {
  handleApiError,
  extractErrorMessage,
  logError,
  reportToMonitoring,
  handleValidationErrors,

  parseApiErrorResponse(response: unknown): Record<string, string> {
    const errors: Record<string, string> = {}

    if (!response) return errors

    try {
      const responseStr = JSON.stringify(response)
      const errorObj = response as Record<string, unknown>

      const emailExistsRegex = /user with email ['"]([^'"]+)['"] already exists/i
      const emailMatch = responseStr.match(emailExistsRegex)
      if (emailMatch) {
        errors.email = `User with email ${emailMatch[1]} already exists`
        return errors
      }

      if (errorObj.message && errorObj.data && typeof errorObj.data === 'object') {
        const dataObj = errorObj.data as Record<string, unknown>

        for (const [key, value] of Object.entries(dataObj)) {
          if (value) {
            errors[key] = typeof value === 'string' ? value : String(value)
          }
        }
      }

      if (Object.keys(errors).length === 0) {
        if (errorObj.data) {
          const dataErrors = this.extractFieldErrors(errorObj.data)
          Object.assign(errors, dataErrors)
        }

        if (errorObj.errors) {
          const errorsData = this.extractFieldErrors(errorObj.errors)
          Object.assign(errors, errorsData)
        }
      }

      if (errorObj.message && typeof errorObj.message === 'string' && Object.keys(errors).length === 0) {
        errors.general = errorObj.message
      }

      return errors
    } catch (error) {
      console.error('Error parsing API error response:', error)
      return { general: 'Error processing server response' }
    }
  },

  extractFieldErrors(data: unknown): Record<string, string> {
    const errors: Record<string, string> = {}

    if (typeof data !== 'object' || data === null) {
      return errors
    }

    const dataObj = data as Record<string, unknown>

    for (const [key, value] of Object.entries(dataObj)) {
      if (typeof value === 'string') {
        errors[key] = value
      } else if (Array.isArray(value) && value.length > 0) {
        errors[key] = typeof value[0] === 'string' ? value[0] : String(value[0])
      } else if (value && typeof value === 'object') {
        const nestedErrors = this.extractFieldErrors(value)
        Object.assign(errors, nestedErrors)
      }
    }

    return errors
  },
}
